import cx from "classnames";
import { isEqual } from "lodash";
import { FC, JSX, useContext, useMemo, useState } from "react";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton, QESButtonTypeClasses } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FlyoutLink } from "@app/shared/Flyouts";
import MedicaidAdvancedFilters from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidAdvancedFilters";
import { MedicaidFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/MedicaidFiltersContext";
import { MedicaidPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidPrimaryFiltersPayload";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";

const MedicaidPrimaryFilters: FC = (): JSX.Element => {
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const medicaidFiltersContext = useContext(MedicaidFiltersContext);

  const [isAdvancedFiltersDirty, setIsAdvancedFiltersDirty] = useState<boolean>(false);

  const hasFilterData = !!benchmarkingFiltersContext.states.length;

  const programDisabled = (): boolean => {
    return medicaidFiltersContext.primaryFiltersLoading
      || !hasFilterData
      || !benchmarkingFiltersContext.selectedState
      || medicaidFiltersContext.programs.length === 0;
  }

  const serviceAreaDisabled = (): boolean => {
    return medicaidFiltersContext.primaryFiltersLoading
      || !hasFilterData
      || !benchmarkingFiltersContext.selectedState
      || !benchmarkingFiltersContext.programKey;
  }

  const applyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (benchmarkingFiltersContext.linesOfBusinessLoading || medicaidFiltersContext.primaryFiltersLoading || medicaidFiltersContext.networkFiltersLoading || !hasFilterData) {
      return true;
    }

    const lineOfBusinessEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.lineOfBusinessId,
      benchmarkingFiltersContext.selectedLineOfBusiness
    );

    const countiesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
      benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
    );

    const membershipCountEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload)?.membershipCount === medicaidFiltersContext.selectedMembershipCount;
    const programsEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload)?.programKey === benchmarkingFiltersContext.programKey;

    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, a line of business, a program, and a membership count are not selected
    const anyChanged = !countiesEqual || !lineOfBusinessEqual || !membershipCountEqual || !programsEqual;
    const allSelected = benchmarkingFiltersContext.selectedCounties.length > 0 &&
      !!benchmarkingFiltersContext.selectedLineOfBusiness &&
      !!medicaidFiltersContext.selectedMembershipCount &&
      !!benchmarkingFiltersContext.programKey;

    return !(anyChanged && allSelected);
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    const loading = benchmarkingFiltersContext.linesOfBusinessLoading || medicaidFiltersContext.primaryFiltersLoading;
    const isDefault = benchmarkingFiltersContext.selectedCounties.length === 0 &&
      !benchmarkingFiltersContext.selectedState &&
      medicaidFiltersContext.selectedMembershipCount === MembershipCountTypeEnum.All &&
      !benchmarkingFiltersContext.programKey;

    return loading || isDefault;
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem = benchmarkingFiltersContext.counties.find(x => x.key === parentKey);
      benchmarkingFiltersContext.setMousedOverCountyIds(cbsa?.children.map(x => x.key));
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      benchmarkingFiltersContext.setMousedOverCountyIds([dropdownItemKey]);
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      benchmarkingFiltersContext.setSelectedState(state);
    }
  }

  // drives the notification pip on the Advanced Filters button — true whenever Membership Count,
  // the only advanced-only field for Medicaid, is non-default
  const showAdvancedFiltersIndicator = useMemo<boolean>(() => {
    return medicaidFiltersContext.selectedMembershipCount !== MembershipCountTypeEnum.All;
  }, [medicaidFiltersContext.selectedMembershipCount]);

  return (
    <div className="ci-sticky-header">
      <div className="row">
        <div className="col-12">
          <label className="label-m mb-2">
            Filters
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <QESSelect
            disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length}
            testId="network-benchmarking-dashboard__primary-filters--line-of-business"
            label="Line of Business"
            onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
            value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
          >
            {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
              <option
                key={index}
                value={lineOfBusiness}
              >
                {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            disabled={medicaidFiltersContext.primaryFiltersLoading || !hasFilterData}
            testId="network-benchmarking-dashboard__primary-filters--state"
            label="State"
            onChange={(e) => onStateChange(e.target.value)}
            value={benchmarkingFiltersContext.selectedState?.stateAbbreviation || ""}
          >
            {benchmarkingFiltersContext.states.map((state, index) => (
              <option
                key={index}
                value={state.stateAbbreviation}
              >
                {state.stateName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            disabled={programDisabled()}
            testId="network-benchmarking-dashboard__primary-filters--program"
            label="Program"
            onChange={(e) => benchmarkingFiltersContext.setProgramKey(e.target.value)}
            value={benchmarkingFiltersContext.programKey || ""}
          >
            {medicaidFiltersContext.programs.map((program, index) => (
              <option
                key={index}
                value={program.programKey}
              >
                {program.programName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <MultiselectNestedDropdown
            containerClassName="d-block"
            customSelectInputText={(!!benchmarkingFiltersContext.rawCounties.length && benchmarkingFiltersContext.rawCounties.length === benchmarkingFiltersContext.selectedCounties.length) ? "All" : null}
            disabled={serviceAreaDisabled()}
            hasDuplicates={true}
            items={benchmarkingFiltersContext.counties}
            label="Service Area"
            onDropdownItemMouseOver={onCountyCheckboxMouseOver}
            onParentMouseOver={onCBSAMouseOver}
            onSelectionChanged={(selectedCounties: string[]) => { benchmarkingFiltersContext.setSelectedCounties(selectedCounties) }}
            selectedItems={benchmarkingFiltersContext.selectedCounties}
            testId="network-benchmarking-dashboard__primary-filters--county"
          />
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <FlyoutLink
              classNames={cx(QESButtonTypeClasses.get("outline"), "position-relative")}
              content={{
                body: <MedicaidAdvancedFilters onDirtyChange={setIsAdvancedFiltersDirty} />,
                title: "Advanced Filters",
              }}
              disabled={medicaidFiltersContext.primaryFiltersLoading || !hasFilterData}
              modalContent={{
                body: "You will lose unsaved changes. Continue with closing the sidebar?",
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                title: "Warning",
              }}
              size="40"
              testId="competitive-insights__primary-filters--advanced-filters"
              warnOnClose={isAdvancedFiltersDirty}
            >
              {showAdvancedFiltersIndicator && <div id="competitive-insights__primary-filters--advanced-filters-pip" className="qes-button__notification-pip" />}
              Advanced Filters
            </FlyoutLink>
          </div>
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <QESButton
              disabled={applyDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--apply"
              qesType="secondary"
              onClick={() => medicaidFiltersContext.onApplyPrimaryFiltersClick()}
            >
              Apply
            </QESButton>

            <QESButton
              disabled={clearDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--clear"
              qesType="secondary-danger"
              onClick={() => medicaidFiltersContext.onClearPrimaryFiltersClick()}
              className="ms-2"
            >
              Clear
            </QESButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MedicaidPrimaryFilters;
